import { useMutation } from "@tanstack/react-query";
import { QueryKeys, client, queryClient } from "queryClient";
import { UpdateUserInput } from "types";

export const useUserUpdate = () => {
  const updateMutation = useMutation({
    mutationFn: async (userInput: UpdateUserInput) => {
      const { data: updatedUser } = await client.models.User.update(userInput);

      return updatedUser;
    },
    // When mutate is called:
    onMutate: async (newUser) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: [QueryKeys.Users, newUser.id],
      });

      await queryClient.cancelQueries({
        queryKey: [QueryKeys.Users],
      });

      // Snapshot the previous value
      const previousUser = queryClient.getQueryData([
        QueryKeys.Users,
        newUser.id,
      ]);

      // Optimistically update to the new value
      if (previousUser) {
        queryClient.setQueryData(
          [QueryKeys.Users, newUser.id],
          /**
           * `newUser` will at first only include updated values for
           * the record. To avoid only rendering optimistic values for updated
           * fields on the UI, include the previous values for all fields:
           */
          { ...previousUser, ...newUser },
        );
      }

      // Return a context with the previous and new user
      return {
        previousUser,
        newUser,
      };
    },
    // If the mutation fails, use the context we returned above
    onError: (err, newUser, context) => {
      console.error("Error updating record:", err, newUser);
      if (context?.previousUser) {
        queryClient.setQueryData(
          [QueryKeys.Users, context.newUser.id],
          context.previousUser,
        );
      }
    },
    // Always refetch after error or success:
    onSettled: (newUser) => {
      if (newUser) {
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.Users, newUser.id],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.Users],
        });
      }
    },
  });

  return updateMutation;
};
